import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CheckCircleIcon, ExclamationCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { supabase } from '../utils/supabaseClient';
import truckImage from '../assets/amesTruck.png';
import acLogo from '../assets/aclogo.png';

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [sessionVerified, setSessionVerified] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Verify that we have a valid recovery session
    // This handles cross-device scenarios (e.g., reset requested on laptop, link opened on phone)
    let isMounted = true;
    let subscription = null;

    const verifyRecoverySession = async () => {
      setVerifying(true);

      // Set up auth state listener FIRST - Supabase may have already processed the URL hash
      // before this component mounted, so we need to catch the event if it hasn't fired yet
      const { data } = supabase.auth.onAuthStateChange((event, session) => {
        if (!isMounted) return;

        if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
          if (session) {
            setSessionVerified(true);
            setVerifying(false);
            // Clean up the URL hash for security
            if (window.location.hash) {
              window.history.replaceState(null, '', window.location.pathname);
            }
          }
        }
      });
      subscription = data.subscription;

      // Check if session already exists (Supabase may have processed tokens before we mounted)
      // Retry a few times since token exchange can take a moment on slower connections
      const maxRetries = 5;
      const retryDelay = 1000; // 1 second between retries

      for (let i = 0; i < maxRetries; i++) {
        if (!isMounted) return;

        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setSessionVerified(true);
          setVerifying(false);
          if (window.location.hash) {
            window.history.replaceState(null, '', window.location.pathname);
          }
          return;
        }

        // If there's still a hash with tokens, Supabase is still processing
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hasTokens = hashParams.get('access_token') || hashParams.get('type') === 'recovery';

        if (!hasTokens && i === 0) {
          // No tokens in URL and no session on first check - likely invalid link
          // But give it one more chance in case of race condition
        }

        if (i < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }

      // After all retries, if still no session, show error
      if (isMounted) {
        setVerifying(false);
        setError('The password reset link is invalid or has expired. Please request a new one.');
      }
    };

    verifyRecoverySession();

    return () => {
      isMounted = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.updateUser({ password });
      
      if (error) throw error;
      
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Render loading state while verifying session
  const renderContent = () => {
    if (verifying) {
      return (
        <div className="text-center">
          <img className="h-12 w-auto mx-auto mb-6" src={acLogo} alt="Ames Connect" />
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying your reset link...</p>
        </div>
      );
    }

    if (!sessionVerified && error) {
      return (
        <div className="bg-white p-8 rounded-2xl shadow-2xl text-center">
          <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <ExclamationCircleIcon className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Link Expired or Invalid</h3>
          <p className="text-gray-600 mb-6">
            {error}
          </p>
          <Link
            to="/login"
            className="w-full inline-block bg-red-600 text-white py-3 px-4 rounded-sm hover:bg-red-700 font-medium text-center"
          >
            Request New Reset Link
          </Link>
        </div>
      );
    }

    if (success) {
      return (
        <div className="bg-white p-8 rounded-2xl shadow-2xl text-center">
          <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircleIcon className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Password Reset Successfully</h3>
          <p className="text-gray-600 mb-6">
            Your password has been updated. You can now sign in with your new password.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-red-600 text-white py-3 px-4 rounded-sm hover:bg-red-700 font-medium"
          >
            Go to Sign In
          </button>
        </div>
      );
    }

    return (
      <div>
        <div className="text-left mb-6">
          <img className="h-12 w-auto" src={acLogo} alt="Ames Connect" />
          <h3 className="mt-6 text-3xl font-extrabold text-gray-900">Set New Password</h3>
          <p className="mt-2 text-sm text-gray-600">
            Enter your new password below to complete the reset.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 pr-10"
                disabled={loading}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                ) : (
                  <EyeIcon className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              disabled={loading}
              required
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-sm">
              <div className="flex">
                <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading || !password || !confirmPassword}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-sm shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          {renderContent()}
        </div>
      </div>
      <div className="hidden lg:flex w-0 flex-1 bg-gray-50 items-center justify-center p-12">
        <div className="w-full max-w-md text-center">
          <img
            className="mx-auto h-auto w-full max-w-sm"
            src={truckImage}
            alt="Ames Profiler Truck"
          />
          <h2 className="mt-8 text-3xl font-extrabold text-gray-900">Pavement Analysis, Perfected.</h2>
          <p className="mt-4 text-lg text-gray-600">
            Leverage data to make smarter decisions about your road infrastructure projects.
          </p>
        </div>
      </div>
    </div>
  );
}